import React, { useEffect, useCallback, useState } from "react";
import Typography from "apollo-react/components/Typography";
import Paper from "apollo-react/components/Paper";
import Box from "apollo-react/components/Box";
import Grid from "apollo-react/components/Grid";
import Divider from "apollo-react/components/Divider";
import { useDrugPricingFormData } from "../../../../../hooks";
import ViewOnlyFileDisplay from "../../../../common/ViewOnlyFileDisplay";
import axiosInstance from '../../../../../axiosInstance';
import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../../../../common/CommentToggle/CommentToggle';
import { STATUSES } from '../../../../../constants/statuses';
import { ROLES } from '../../../../../constants/roles';
import { CATEGORIES } from '../../../../../constants/categories';

// Display components for clean data presentation
const DataItem = ({ label, value, variant = "body1" }) => (
  <Box sx={{ marginBottom: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 0.5 }}>
      {label}
    </Typography>
    <Typography variant={variant} sx={{ color: value ? '#333' : '#999', fontStyle: !value ? 'italic' : 'normal' }}>
      {value || 'Not provided'}
    </Typography>
  </Box>
);

const SectionHeader = ({ title, icon }) => (
  <Box sx={{ marginBottom: 3, marginTop: 4 }}>
    <Typography
      variant="h6"
      component="h2"
      sx={{
        fontWeight: 600,
        color: "",
        marginBottom: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      {icon && <span>{icon}</span>}
      {title}
    </Typography>
    <Divider sx={{ borderColor: '#e0e6ed' }} />
  </Box>
);

/**
 * View-only component for displaying drug pricing form data
 * Fetches data from API and displays in read-only format with download links
 */
/**
 * Transform flat snapshot data (database model) to nested API format
 */
const transformSnapshotToApiFormat = (snapshot, fetchedDetails = {}) => {
  if (!snapshot) return null;
  
  // Helper to transform file details from snapshot format to API format
  // Handles both new format (with _details) and old format (just IDs)
  const transformFileDetails = (fileDetails, fileId, fetchedKey) => {
    // New format: full file details object from snapshot
    if (fileDetails && typeof fileDetails === 'object') {
      return {
        file_id: fileDetails.id,
        file_name: fileDetails.original_name,
        file_size: fileDetails.file_size,
        mime_type: fileDetails.mime_type,
        file_path: fileDetails.file_path
      };
    }
    
    // Check if we have fetched details for this file (legacy snapshots)
    if (fetchedDetails[fetchedKey]) {
      const fetched = fetchedDetails[fetchedKey];
      return {
        file_id: fetched.id,
        file_name: fetched.original_name,
        file_size: fetched.file_size,
        mime_type: fetched.mime_type,
        file_path: fetched.file_path
      };
    }
    
    return null;
  };
  
  return {
    product_information: {
      brand_name: snapshot.drug_name_brand,
      generic_name: snapshot.generic_name,
      dosage_form_and_strength: {
        dosage_form: snapshot.dosage_form,
        strength: snapshot.strength
      },
      manufacturer_details: {
        manufacturer_name: snapshot.manufacturer_name,
        marketing_authorization_holder: snapshot.marketing_authorization_holder,
        local_agent_distributor: snapshot.local_agent_distributor
      },
      country_of_origin: snapshot.country_of_origin,
      packaging_details: snapshot.packaging_details,
      contact_information: {
        contact_email: snapshot.contact_email,
        phone_number: snapshot.phone_number
      }
    },
    pricing_details: {
      proposed_price_local_currency: {
        amount: snapshot.proposed_price_local,
        currency: snapshot.currency || ''
      },
      cif_price: {
        amount: snapshot.cif_price,
        description: 'Cost, Insurance, Freight price'
      },
      ex_factory_price: {
        amount: snapshot.ex_factory_price,
        description: 'Ex-factory price (manufacturer price)'
      },
      reference_prices_other_countries: {
        markets: snapshot.reference_country_prices || [],
        description: 'Reference prices from GCC, EU, US and other markets'
      },
      price_justification: snapshot.remarks_justification
    },
    supporting_documents: {
      price_certificate: transformFileDetails(
        snapshot.price_certificate_file_details, 
        snapshot.price_certificate_file_id,
        'price_certificate'
      ),
      gmp_certificate: transformFileDetails(
        snapshot.gmp_certificate_file_details,
        snapshot.gmp_certificate_file_id,
        'gmp_certificate'
      ),
      market_authorization_certificate: transformFileDetails(
        snapshot.market_authorization_certificate_file_details,
        snapshot.market_authorization_certificate_file_id,
        'market_authorization'
      ),
      packaging_samples: transformFileDetails(
        snapshot.packaging_samples_file_details,
        snapshot.packaging_samples_file_id,
        'packaging_samples'
      ),
      pricing_history: transformFileDetails(
        snapshot.pricing_history_file_details,
        snapshot.pricing_history_file_id,
        'pricing_history'
      )
    },
    submission_date: snapshot.submission_date,
    createdAt: snapshot.createdAt,
    updatedAt: snapshot.updatedAt,
    submitted_by: snapshot.end_user_id ? {
      user_id: snapshot.end_user_id,
      name: null,
      email: null,
      username: null
    } : null
  };
};

const DrugPricingFormView = ({ 
  drugPricingFormId, 
  drugPricingFormSnapshot = null,
  persona = 'customer', role = '', requestProgressStatus = '', comments = [], setComments = () => {},
  enableAddComment = true
}) => {
  // State to store fetched file details for legacy snapshots
  const [fetchedFileDetails, setFetchedFileDetails] = useState({});
  const [fetchingFiles, setFetchingFiles] = useState(false);
  
  // Fetch data for view mode (skip if snapshot is provided)
  const { 
    data: viewData, 
    loading: viewLoading, 
    error: viewError 
  } = useDrugPricingFormData(drugPricingFormId, persona, !drugPricingFormSnapshot);

  // Fetch file metadata for legacy snapshots that only have file IDs
  useEffect(() => {
    const fetchLegacyFileDetails = async () => {
      if (!drugPricingFormSnapshot) {
        return;
      }
      
      const fileFields = [
        { idField: 'price_certificate_file_id', detailsField: 'price_certificate_file_details', key: 'price_certificate' },
        { idField: 'gmp_certificate_file_id', detailsField: 'gmp_certificate_file_details', key: 'gmp_certificate' },
        { idField: 'market_authorization_certificate_file_id', detailsField: 'market_authorization_certificate_file_details', key: 'market_authorization' },
        { idField: 'packaging_samples_file_id', detailsField: 'packaging_samples_file_details', key: 'packaging_samples' },
        { idField: 'pricing_history_file_id', detailsField: 'pricing_history_file_details', key: 'pricing_history' }
      ];
      
      const filesToFetch = fileFields.filter(field => 
        drugPricingFormSnapshot[field.idField] && !drugPricingFormSnapshot[field.detailsField]
      );
      
      if (filesToFetch.length === 0) {
        return;
      }
      
      setFetchingFiles(true);
      const newFileDetails = {};
      
      try {
        await Promise.all(
          filesToFetch.map(async (field) => {
            try {
              const fileId = drugPricingFormSnapshot[field.idField];
              
              const endpoint = persona === 'customer' 
                ? `/api/files/${fileId}`
                : `/api/files/admin/${fileId}`;
              
              const response = await axiosInstance.get(endpoint);
              
              if (response.data.success && response.data.data) {
                const fileInfo = response.data.data;
                newFileDetails[field.key] = {
                  id: fileInfo.id,
                  original_name: fileInfo.original_name || fileInfo.name,
                  file_size: fileInfo.file_size || fileInfo.size,
                  mime_type: fileInfo.mime_type || fileInfo.type,
                  file_path: fileInfo.file_path
                };
              }
            } catch (err) {
              console.error(`Error fetching file details for ${field.key}:`, err);
            }
          })
        );
        
        setFetchedFileDetails(newFileDetails);
      } catch (error) {
        console.error('Error fetching legacy file details:', error);
      } finally {
        setFetchingFiles(false);
      }
    };
    
    fetchLegacyFileDetails();
  }, [drugPricingFormSnapshot, persona]);

  const fetchComments = useCallback(async () => {
        // Don't fetch comments in snapshot mode (read-only historical view)
        if (!drugPricingFormId || drugPricingFormSnapshot) return;
        const getCommentsLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
        const response = await axiosInstance.get(`/api/drug-pricing-form-comments/${getCommentsLink}/${drugPricingFormId}`);
        setComments(response.data || []);
    }, [drugPricingFormId, role, drugPricingFormSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
        if (drugPricingFormId && !drugPricingFormSnapshot) {
            fetchComments();
        }
    }, [drugPricingFormId, fetchComments, drugPricingFormSnapshot]);

  const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
    try {
        const addCommentLink = role === "customer" ? "comments/enduser" : "";
        
        const requestData = {
            drugPricingFormId,
            comment,
        };
        
        // Add file ID if provided
        if (fileId) {
            requestData.additional_file_id = fileId;
        }

        const response = await axiosInstance.post(`/api/drug-pricing-form-comments/${addCommentLink}`, requestData);

        // Enrich the response with file data if available
        // set isOwner to true since the current user just created this comment
        const enrichedComment = { ...response.data, isOwner: true };
        if (fileData && fileId) {
            // Convert fileData to the expected additional_file format
            enrichedComment.additional_file = {
                id: fileData.id,
                original_name: fileData.original_name,
                file_path: null, // Not provided by upload response
                file_size: fileData.file_size,
                mime_type: fileData.mime_type,
                createdAt: fileData.created_at,
                updatedAt: fileData.created_at,
                deletedAt: null,
                user_id: null, // Will be set by backend
                end_user_id: null, // Will be set by backend
                ...fileData // Include any additional fields
            };
        }

        console.log("Comment added with file data:", enrichedComment);
        setComments(prev => [...prev, enrichedComment]);
    } catch (error) {
        console.error("Error adding comment:", error);
        throw error;
    }
  };

  // handle edit comment
  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const editLink = role === "customer" ? "enduser/" : "";
      const requestData = { comment: newText };
      
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      await axiosInstance.put(`/api/drug-pricing-form-comments/${editLink}${commentId}`, requestData);
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  };

  // handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      const deleteLink = role === "customer" ? "enduser/" : "";
      await axiosInstance.delete(`/api/drug-pricing-form-comments/${deleteLink}${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  };

  // Show loading state (only if not using snapshot or if fetching file details)
  if ((!drugPricingFormSnapshot && viewLoading) || (drugPricingFormSnapshot && fetchingFiles)) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography>
            {fetchingFiles ? 'Loading file details...' : 'Loading drug pricing form...'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Show error state (only if not using snapshot)
  if (!drugPricingFormSnapshot && viewError) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Box sx={{ padding: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Form
          </Typography>
          <Typography color="error">
            {viewError}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Use snapshot data if provided, otherwise use fetched data
  const data = drugPricingFormSnapshot 
    ? transformSnapshotToApiFormat(drugPricingFormSnapshot, fetchedFileDetails)
    : (viewData || {});

  // Extract nested data with safe access
  const productInfo = data.product_information || {};
  const dosageInfo = productInfo.dosage_form_and_strength || {};
  const manufacturerInfo = productInfo.manufacturer_details || {};
  const contactInfo = productInfo.contact_information || {};
  const pricingInfo = data.pricing_details || {};
  const proposedPrice = pricingInfo.proposed_price_local_currency || {};
  const cifPrice = pricingInfo.cif_price || {};
  const exFactoryPrice = pricingInfo.ex_factory_price || {};
  const referencePrice = pricingInfo.reference_prices_other_countries || {};
  const supportingDocs = data.supporting_documents || {};
  const submittedBy = data.submitted_by || {};

  return (
    <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
      {/* Header with submission info */}
      <Box sx={{ marginBottom: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '', marginBottom: 2 }}>
          Drug Pricing Form
        </Typography>
        {data.submission_date && (
          <Typography variant="body2" color="text.secondary">
            Submitted on: {new Date(data.submission_date).toLocaleDateString('en-GB')} at {new Date(data.submission_date).toLocaleTimeString()}
          </Typography>
        )}
        {submittedBy.name && (
          <Typography variant="body2" color="text.secondary">
            By: {submittedBy.name} ({submittedBy.email})
          </Typography>
        )}
      </Box>

      {/* Product Information */}
      <SectionHeader title="Product Information" />
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <DataItem label="Brand Name" value={productInfo.brand_name} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Generic Name" value={productInfo.generic_name} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Dosage Form" value={dosageInfo.dosage_form} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Strength" value={dosageInfo.strength} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Manufacturer Name" value={manufacturerInfo.manufacturer_name} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Marketing Authorization Holder" value={manufacturerInfo.marketing_authorization_holder} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Local Agent/Distributor" value={manufacturerInfo.local_agent_distributor} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem label="Country of Origin" value={productInfo.country_of_origin} />
        </Grid>
        {productInfo.packaging_details && (
          <Grid item xs={12}>
            <DataItem label="Packaging Details" value={productInfo.packaging_details} />
          </Grid>
        )}
      </Grid>

      {/* Pricing Details */}
      <SectionHeader title="Pricing Details" />
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <DataItem 
            label="Proposed Price in Local Currency" 
            value={proposedPrice.amount ? `${proposedPrice.amount} ${proposedPrice.currency}` : null} 
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem 
            label="CIF (Cost, Insurance, Freight) Price" 
            value={cifPrice.amount ? `${cifPrice.amount}` : null}
          />
          {cifPrice.description && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
              {cifPrice.description}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <DataItem 
            label="Ex-Factory Price" 
            value={exFactoryPrice.amount ? `${exFactoryPrice.amount}` : null}
          />
          {exFactoryPrice.description && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
              {exFactoryPrice.description}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
            Reference Prices from Other Countries (e.g., GCC, EU, US)
          </Typography>
          {(() => {
            const validMarkets = (referencePrice.markets || []).filter(
              market => market.country || market.price || market.currency
            );
            
            return validMarkets.length > 0 ? (
              <Box sx={{ marginBottom: 2 }}>
                {validMarkets.map((market, index) => (
                  <Box key={index} sx={{ marginBottom: 1, padding: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Country: {market.country}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">Price: {market.price}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">Currency: {market.currency}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {referencePrice.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                    {referencePrice.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            );
          })()}
        </Grid>
        {pricingInfo.price_justification && (
          <Grid item xs={12}>
            <DataItem label="Price Justification" value={pricingInfo.price_justification} />
          </Grid>
        )}
      </Grid>

      {/* Supporting Documents */}
      <SectionHeader title="Supporting Documents" />
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
              Price Certificate from Country of Origin
            </Typography>
            {supportingDocs.price_certificate ? (
              <ViewOnlyFileDisplay
                files={[{
                  id: supportingDocs.price_certificate.file_id,
                  file_id: supportingDocs.price_certificate.file_id,
                  name: supportingDocs.price_certificate.file_name,
                  file_name: supportingDocs.price_certificate.file_name,
                  file_size: supportingDocs.price_certificate.file_size,
                  mime_type: supportingDocs.price_certificate.mime_type
                }]}
                label=""
                helperText={supportingDocs.price_certificate.description || "Download price certificate"}
                persona={persona}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
              Pricing History in Other Markets
            </Typography>
            {supportingDocs.pricing_history ? (
              <ViewOnlyFileDisplay
                files={[{
                  id: supportingDocs.pricing_history.file_id,
                  file_id: supportingDocs.pricing_history.file_id,
                  name: supportingDocs.pricing_history.file_name,
                  file_name: supportingDocs.pricing_history.file_name,
                  file_size: supportingDocs.pricing_history.file_size,
                  mime_type: supportingDocs.pricing_history.mime_type
                }]}
                label=""
                helperText={supportingDocs.pricing_history.description || "Download pricing history"}
                persona={persona}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
              Market Authorization Certificate
            </Typography>
            {supportingDocs.market_authorization_certificate ? (
              <ViewOnlyFileDisplay
                files={[{
                  id: supportingDocs.market_authorization_certificate.file_id,
                  file_id: supportingDocs.market_authorization_certificate.file_id,
                  name: supportingDocs.market_authorization_certificate.file_name,
                  file_name: supportingDocs.market_authorization_certificate.file_name,
                  file_size: supportingDocs.market_authorization_certificate.file_size,
                  mime_type: supportingDocs.market_authorization_certificate.mime_type
                }]}
                label=""
                helperText={supportingDocs.market_authorization_certificate.description || "Download market authorization certificate"}
                persona={persona}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
              GMP Certificate
            </Typography>
            {supportingDocs.gmp_certificate ? (
              <ViewOnlyFileDisplay
                files={[{
                  id: supportingDocs.gmp_certificate.file_id,
                  file_id: supportingDocs.gmp_certificate.file_id,
                  name: supportingDocs.gmp_certificate.file_name,
                  file_name: supportingDocs.gmp_certificate.file_name,
                  file_size: supportingDocs.gmp_certificate.file_size,
                  mime_type: supportingDocs.gmp_certificate.mime_type
                }]}
                label=""
                helperText={supportingDocs.gmp_certificate.description || "Download GMP certificate"}
                persona={persona}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
              Packaging and Labeling Samples
            </Typography>
            {supportingDocs.packaging_samples ? (
              <ViewOnlyFileDisplay
                files={[{
                  id: supportingDocs.packaging_samples.file_id,
                  file_id: supportingDocs.packaging_samples.file_id,
                  name: supportingDocs.packaging_samples.file_name,
                  file_name: supportingDocs.packaging_samples.file_name,
                  file_size: supportingDocs.packaging_samples.file_size,
                  mime_type: supportingDocs.packaging_samples.mime_type
                }]}
                label=""
                helperText={supportingDocs.packaging_samples.description || "Download packaging samples"}
                persona={persona}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                Not provided
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Contact Information */}
      {(contactInfo.contact_email || contactInfo.phone_number) && (
        <>
          <SectionHeader title="Contact Information" />
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <DataItem label="Contact Email" value={contactInfo.contact_email} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DataItem label="Phone Number" value={contactInfo.phone_number} />
            </Grid>
          </Grid>
        </>
      )}
      {/* Comments Section */}
      <div 
        className="form-comment-section" 
        style={{ 
          isolation: 'isolate', 
          position: 'relative', 
          zIndex: 1,
          marginTop: '35px'
        }}
      >
        <CommentsList
            prevComments={comments}
            pagination={{}}
            filters={{}}
            updateFilters={() => { }}
            persona={role}
            showControls={false}
            showFilters={false}
            showSorting={false}
            showPageSize={false}
            showResultsInfo={false}
            showPagination={false}
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
        />
        {enableAddComment && requestProgressStatus === STATUSES.ROLE_STATUSES[ROLES.pricing].ACTIVE &&
          role && [ROLES.pricing].findIndex(r => r === role) !== -1 && (
            <CommentToggle
              onAddComment={handleAddComment}
              persona={(role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "customer" : "admin"}
              buttonText="Comment"
              buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
              hasComments={comments && comments.length > 0}
            />
          )}
      </div>
    </Paper>
  );
};

export default DrugPricingFormView;
